import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { theme } from '../config/theme';
import { locations } from '../config/locations';
import Footer from '../components/Footer';
import BackButton from '../components/BackButton';
import { useBackButton } from '../contexts/BackButtonContext';
import { Button } from '../junto-frontend/components/ui';

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: ${theme.colors.primary.background};
  padding-top: 128px;
  box-sizing: border-box;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding-top: 96px;
  }
`;

const ContentContainer = styled(motion.section)`
  padding: ${theme.spacing.sectionPadding.vertical} ${theme.spacing.sectionPadding.horizontal};
  background-color: ${theme.colors.primary.background};
  max-width: 1000px;
  margin: 0 auto;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding-top: ${theme.spacing.md};
  }
`;

const Header = styled(motion.div)`
  text-align: center;
  margin-bottom: ${theme.spacing.xl};
`;

const Title = styled(motion.h1)`
  font-family: ${theme.typography.primary.fontFamily};
  font-size: ${theme.typography.primary.largeHeading.fontSize};
  font-weight: ${theme.typography.primary.largeHeading.fontWeight};
  color: ${theme.colors.primary.text};
  margin-bottom: ${theme.spacing.lg};
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: ${theme.typography.primary.mediumHeading.fontSize};
  }
`;

const Subtitle = styled(motion.p)`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: ${theme.typography.secondary.body.fontSize};
  color: ${theme.colors.primary.text};
  opacity: 0.8;
  max-width: 600px;
  margin: 0 auto ${theme.spacing.xl};
  line-height: 1.6;
`;

const SectionDivider = styled.div`
  width: 60px;
  height: 1px;
  background-color: ${theme.colors.accent.bright};
  margin: ${theme.spacing.xl} auto;
`;

const BenefitsSection = styled(motion.div)`
  margin-bottom: ${theme.spacing.lg};
  text-align: center;
`;

const BenefitsTitle = styled.h3`
  font-family: ${theme.typography.primary.fontFamily};
  font-size: ${theme.typography.primary.heading.fontSize};
  color: ${theme.colors.primary.text};
  margin-bottom: ${theme.spacing.xl};
  text-align: center;
`;

const BenefitsList = styled.ul`
  list-style: none;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: ${theme.spacing.md} ${theme.spacing.lg};
  justify-items: start;
  max-width: 800px;
  margin: 0 auto;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing.md};
  }
`;

const BenefitItem = styled.li`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: ${theme.typography.secondary.body.fontSize};
  color: ${theme.colors.primary.text};
  display: flex;
  align-items: flex-start;
  width: 100%;
  line-height: 1.5;
  
  &:before {
    content: "✓";
    color: ${theme.colors.accent.main};
    font-weight: bold;
    margin-right: ${theme.spacing.sm};
    margin-top: 2px;
    flex-shrink: 0;
  }
`;

const FormSection = styled(motion.div)`
  text-align: center;
`;

const FormTitle = styled.h3`
  font-family: ${theme.typography.primary.fontFamily};
  font-size: ${theme.typography.primary.heading.fontSize};
  color: ${theme.colors.primary.text};
  margin-bottom: ${theme.spacing.xl};
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
  max-width: 600px;
  margin: 0 auto;
  text-align: left;
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: ${theme.typography.secondary.body.fontSize};
  color: ${theme.colors.primary.text};
  margin-bottom: ${theme.spacing.sm};
  font-weight: 500;
`;

const Input = styled.input`
  background: transparent;
  border: none;
  border-bottom: 1px solid ${theme.colors.primary.ui_dark};
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 0.9rem;
  color: ${theme.colors.primary.text};
  padding: ${theme.spacing.sm} 0;
  opacity: 0.7;
  transition: ${theme.transitions.default};
  
  &::placeholder {
    color: ${theme.colors.primary.text};
    opacity: 0.5;
    font-style: italic;
  }
  
  &:focus {
    outline: none;
    border-bottom-color: ${theme.colors.accent.main};
    opacity: 1;
  }
  
  &:hover {
    transform: scale(1.02);
  }
`;

const TextArea = styled.textarea`
  background: transparent;
  border: none;
  border-bottom: 1px solid ${theme.colors.primary.ui_dark};
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 0.9rem;
  color: ${theme.colors.primary.text};
  padding: ${theme.spacing.sm} 0;
  opacity: 0.7;
  transition: ${theme.transitions.default};
  resize: none;
  min-height: 60px;
  
  &::placeholder {
    color: ${theme.colors.primary.text};
    opacity: 0.5;
    font-style: italic;
  }
  
  &:focus {
    outline: none;
    border-bottom-color: ${theme.colors.accent.main};
    opacity: 1;
  }
  
  &:hover {
    transform: scale(1.02);
  }
`;

const FileUpload = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

const FileInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  z-index: 2;
`;

const FileUploadButton = styled.div`
  border: 2px dashed ${theme.colors.primary.ui_dark};
  border-radius: 4px;
  padding: ${theme.spacing.lg};
  text-align: center;
  cursor: pointer;
  transition: ${theme.transitions.default};
  position: relative;
  z-index: 1;
  
  &:hover {
    border-color: ${theme.colors.accent.main};
    background: ${theme.colors.neutral.light};
  }
`;

const FileUploadText = styled.div`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: ${theme.typography.secondary.body.fontSize};
  color: ${theme.colors.primary.text};
  opacity: 0.7;
`;

const FileName = styled.div`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 0.85rem;
  color: ${theme.colors.accent.main};
  margin-top: ${theme.spacing.sm};
`;

const SubmitButtonWrapper = styled.div`
  align-self: center;
  margin-top: ${theme.spacing.lg};
`;

const StatusMessage = styled.div`
  font-family: ${theme.typography.secondary.fontFamily};
  font-size: 0.9rem;
  text-align: center;
  margin-top: ${theme.spacing.md};
  color: ${props => props.$error ? theme.colors.accent.deep : theme.colors.accent.main};
  opacity: ${props => props.$show ? 1 : 0};
  transition: opacity 0.3s ease;
`;

const JoinOurTeamPage = () => {
  const { setBackButton } = useBackButton();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    experience: '',
    message: '',
    resume: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState({ show: false, message: '', error: false });
  const primaryLocation = locations[0];

  useEffect(() => {
    setBackButton("← Back to Our Instructors", "/#team");
    
    const timer = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);

    return () => clearTimeout(timer);
  }, [setBackButton]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setStatus({
          show: true,
          message: 'File size must be less than 5MB',
          error: true
        });
        setTimeout(() => setStatus(prev => ({ ...prev, show: false })), 3000);
        return;
      }
      
      // Check file type
      const allowedTypes = ['.pdf', '.doc', '.docx'];
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      if (!allowedTypes.includes(fileExtension)) {
        setStatus({
          show: true,
          message: 'Please upload a PDF or Word document',
          error: true
        });
        setTimeout(() => setStatus(prev => ({ ...prev, show: false })), 3000);
        return;
      }
      
      setFormData({...formData, resume: file});
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name) {
      setStatus({
        show: true,
        message: 'Name is required',
        error: true
      });
      setTimeout(() => setStatus(prev => ({ ...prev, show: false })), 3000);
      return;
    }

    if (!formData.email || !validateEmail(formData.email)) {
      setStatus({
        show: true,
        message: 'Please enter a valid email address',
        error: true
      });
      setTimeout(() => setStatus(prev => ({ ...prev, show: false })), 3000);
      return;
    }


    setIsSubmitting(true);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone || 'Not provided');
      formDataToSend.append('experience', formData.experience || 'Not provided');
      formDataToSend.append('message', formData.message);
      formDataToSend.append('_replyto', formData.email);
      formDataToSend.append('_subject', `Join Our Team Application from ${formData.name}`);
      formDataToSend.append('_captcha', 'false');
      
      if (formData.resume) {
        formDataToSend.append('resume', formData.resume);
      }

      const response = await fetch(`https://formsubmit.co/${primaryLocation.contact.email}`, {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setStatus({
          show: true,
          message: 'Application sent successfully! We\'ll be in touch soon.',
          error: false
        });

        // Reset form
        setFormData({ name: '', email: '', phone: '', experience: '', message: '', resume: null });
      } else {
        throw new Error('Form submission failed');
      }
      
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus({
        show: true,
        message: 'Failed to send application. Please try again.',
        error: true
      });
    }

    setIsSubmitting(false);
    setTimeout(() => setStatus(prev => ({ ...prev, show: false })), 5000);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  return (
    <PageContainer>
      <BackButton 
        text="← Back to Our Instructors" 
        to="/#team"
      />
      
      <ContentContainer
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <Header>
          <Title variants={itemVariants}>
            Join Our <em>Team</em>
          </Title>
          <Subtitle variants={itemVariants}>
            Passionate about Pilates and helping others transform their lives through movement? 
            We're always looking for talented instructors to join our growing team.
          </Subtitle>
        </Header>

        <BenefitsSection variants={itemVariants}>
          <BenefitsTitle>What We Offer</BenefitsTitle>
          <BenefitsList>
            <BenefitItem>Competitive rates</BenefitItem>
            <BenefitItem>Flexible scheduling</BenefitItem>
            <BenefitItem>Continuing education programs</BenefitItem>
            <BenefitItem>Training reimbursement</BenefitItem>
            <BenefitItem>Supportive team environment</BenefitItem>
            <BenefitItem>Professional development opportunities</BenefitItem>
          </BenefitsList>
        </BenefitsSection>

        <SectionDivider />

        <FormSection variants={itemVariants}>
          <FormTitle>Apply Today</FormTitle>
          <Form onSubmit={handleSubmit}>
            <FormField>
              <Label htmlFor="name">Full Name *</Label>
              <Input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                placeholder="Your full name"
                required
              />
            </FormField>

            <FormField>
              <Label htmlFor="email">Email Address *</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="your.email@example.com"
                required
              />
            </FormField>

            <FormField>
              <Label htmlFor="phone">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                placeholder="(555) 123-4567"
              />
            </FormField>

            <FormField>
              <Label htmlFor="experience">Background & Experience</Label>
              <TextArea
                id="experience"
                value={formData.experience}
                onChange={(e) => setFormData({...formData, experience: e.target.value})}
                placeholder="Tell us about your background, experience, and what makes you a great fit..."
              />
            </FormField>

            <FormField>
              <Label htmlFor="message">Additional Information</Label>
              <TextArea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                placeholder="Share anything else you'd like us to know about you..."
              />
            </FormField>

            <FormField>
              <Label htmlFor="resume">Resume (PDF or Word document, max 5MB)</Label>
              <FileUpload>
                <FileUploadButton>
                  <FileInput
                    id="resume"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                  />
                  <FileUploadText>
                    {formData.resume ? 'Click to change file' : 'Click to upload your resume'}
                  </FileUploadText>
                </FileUploadButton>
                {formData.resume && (
                  <FileName>Selected: {formData.resume.name}</FileName>
                )}
              </FileUpload>
            </FormField>

            <SubmitButtonWrapper>
              <Button 
                type="submit" 
                disabled={isSubmitting}
                size="medium"
              >
                {isSubmitting ? 'Sending Application...' : 'Submit Application'}
              </Button>
            </SubmitButtonWrapper>

            <StatusMessage $show={status.show} $error={status.error}>
              {status.message}
            </StatusMessage>
          </Form>
        </FormSection>
      </ContentContainer>
      
      <Footer />
    </PageContainer>
  );
};

export default JoinOurTeamPage;